import { memo, useEffect, useRef, useState } from 'react'
import { Play } from 'lucide-react'
import { posterUrl } from '../lib/format'
import type { MetaPreview } from '../types'

interface Props {
  item: MetaPreview
  onSelect: (item: MetaPreview) => void
  /** overrides the default "open the page" action, e.g. resume playback */
  onActivate?: () => void
  /** replaces the year line, e.g. "S2 E4 · 22 min left" */
  sublabel?: string
  progress?: number
  autofocus?: boolean
  virtualIndex?: number
}

/** Cinemeta returns 16:9 and square art alongside 2:3 posters. Cropping those
 *  through the middle loses the title, so wide art is letterboxed instead. */
const PORTRAIT_LIMIT = 0.78

/** Rows stay mounted behind the player, so every card is diffed on each of
 * its parent's renders. Nothing here depends on anything but its own props. */
export const MediaCard = memo(function MediaCard({ item, onSelect, onActivate, sublabel, progress, autofocus, virtualIndex }: Props) {
  const [wide, setWide] = useState(false)
  const [loadPoster, setLoadPoster] = useState(() => typeof IntersectionObserver === 'undefined')
  const ref = useRef<HTMLButtonElement>(null)
  const caption = sublabel || item.releaseInfo || item.year || (item.type === 'series' ? 'Series' : 'Movie')
  const poster = posterUrl(item.poster)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof IntersectionObserver === 'undefined') { setLoadPoster(true); return }
    const group = element.closest<HTMLElement>('[data-nav-group]')
    const root = group && (group.scrollWidth > group.clientWidth || group.scrollHeight > group.clientHeight) ? group : null
    // Decode the visible six plus a small look-ahead, not two whole TV widths
    // per row. Buttons remain mounted for deterministic D-pad structure while
    // off-window poster bitmaps are released.
    const observer = new IntersectionObserver(([entry]) => setLoadPoster(entry.isIntersecting), { root, rootMargin: '35%' })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return (
    <button
      ref={ref}
      className="media-card"
      data-focusable
      data-autofocus={autofocus ? '' : undefined}
      data-nav-index={virtualIndex}
      onClick={() => (onActivate ? onActivate() : onSelect(item))}
      aria-label={`${item.name}, ${caption}`}
    >
      <span className={`poster-wrap${wide ? ' is-wide' : ''}`}>
        {poster && loadPoster ? (
          <>
            {wide ? <span className="poster-fill" style={{ backgroundImage: `url(${poster})` }} /> : null}
            <img
              src={poster}
              alt=""
              loading="lazy"
              decoding="async"
              width={268}
              height={402}
              onLoad={(event) => {
                const image = event.currentTarget
                if (image.naturalWidth && image.naturalHeight / image.naturalWidth < 1 / PORTRAIT_LIMIT) setWide(true)
              }}
            />
          </>
        ) : <span className="poster-fallback" />}
        <span className="card-play"><Play fill="currentColor" size="1.75rem" /></span>
        <span className="card-caption">
          <strong>{item.name}</strong>
          <small>{caption}</small>
        </span>
        {typeof progress === 'number' && progress > 0 ? (
          <span className="card-progress"><i style={{ width: `${Math.min(100, progress * 100)}%` }} /></span>
        ) : null}
      </span>
    </button>
  )
})
